import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, X, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { ensureProfile } from '../services/profileService';
export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signInWithGoogle
  } = useAuth();
  const getInitialView = (): 'login' | 'signup' | 'forgot' => {
    if (location.pathname === '/login') return 'login';
    if (location.pathname === '/signup') return 'signup';
    return 'signup';
  };
  const [view, setView] = useState<'login' | 'signup' | 'forgot'>(getInitialView());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resetOtpVerified, setResetOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  // Helper function to format error messages with troubleshooting tips
  const formatError = (err: any): string => {
    const errorMessage = err.message || err.toString();
    // Database error saving new user - trigger failure
    if (errorMessage.includes('Database error saving new user') || errorMessage.includes('database error') || errorMessage.includes('Database error')) {
      return 'Account setup error: There was a problem creating your account. Please try again. If the issue persists, try using Google Sign-In instead.';
    }
    // Network/fetch errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('fetch')) {
      return 'Connection error: Unable to reach authentication server. Please check your internet connection and try again. If using a VPN or ad blocker, try disabling it temporarily.';
    }
    // CORS errors
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      return 'Connection blocked: Your browser is blocking the authentication request. Please check your browser extensions or try a different browser.';
    }
    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      return 'Request timeout: The server took too long to respond. Please check your internet connection and try again.';
    }
    // Return original message for other errors
    return errorMessage;
  };
  useEffect(() => {
    if (location.pathname === '/login') {
      setView('login');
      resetForm();
    } else if (location.pathname === '/signup') {
      setView('signup');
      resetForm();
    }
  }, [location.pathname]);
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    return requirements;
  };
  const passwordRequirements = validatePassword(newPassword || password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setOtpSent(false);
    setResetOtpVerified(false);
    setError(null);
    setSuccess(null);
    setShowPasswordRequirements(false);
  };
  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    // Track Google OAuth initiation
    await trackCriticalEvent('Google_OAuth_Initiated', {
      auth_action: view === 'signup' ? 'signup' : 'login',
      page_location: 'auth_page'
    });
    try {
      await signInWithGoogle();
      // User will be redirected to Google OAuth
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(formatError(err));
      setGoogleLoading(false);
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`
          }
        }
      });
      if (error) throw error;
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists');
        return;
      }
      // Fire-and-forget: Call Cloudflare bot protect edge function
      supabase.functions.invoke('cloudflare-bot-protect').catch(err => {
        console.error('[Bot Protection] Edge function failed silently:', err);
      });
      // Supabase sends a 6-digit OTP code to the user's email
      // Switch to the OTP input screen so the user can verify
      setOtpSent(true);
      setSuccess('Verification code sent! Please check your email and enter the 6-digit code below.');
    } catch (err: any) {
      console.error('Signup error:', err);
      console.error('Signup error code:', err?.code, '| status:', err?.status, '| message:', err?.message);
      if (err?.message?.toLowerCase().includes('unexpected') || err?.code === 'unexpected_failure') {
        console.warn('[KNOWN ISSUE] Unexpected Failure detected during signup. ' + 'The user may have been created successfully despite this error. ' + 'Check the Authentication tab in Supabase to verify. ' + 'This was previously caused by broken Welcome Email triggers (now purged).');
      }
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup'
      });
      if (error) throw error;
      if (data.session) {
        // Ensure profile exists in the profiles table (safety net for trigger failures)
        const userId = data.session.user.id;
        await ensureProfile(userId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          fullName: `${firstName.trim()} ${lastName.trim()}`,
          email: email
        });
        // Track successful signup
        const emailDomain = email.split('@')[1];
        await trackCriticalEvent('User_Signed_Up', {
          signup_method: 'email',
          first_name: firstName,
          last_name: lastName,
          email_domain: emailDomain,
          has_password_requirements_met: true
        });
        // New users go to onboarding first
        navigate('/onboarding');
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      if (data.session) {
        // Track successful login
        await trackCriticalEvent('User_Logged_In', {
          login_method: 'email',
          is_returning_user: true
        });
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleSendResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`
      });
      if (error) throw error;
      // Track password reset request
      const emailDomain = email.split('@')[1];
      await trackCriticalEvent('Password_Reset_Requested', {
        email_domain: emailDomain
      });
      setOtpSent(true);
      setSuccess('Reset code sent! Please check your email and enter the code below.');
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery'
      });
      if (error) throw error;
      setResetOtpVerified(true);
      setSuccess('Code verified! Now enter your new password.');
      setOtp('');
    } catch (err: any) {
      console.error('Reset OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      // Track password reset completion
      await trackCriticalEvent('Password_Reset_Completed', {
        reset_method: 'otp_verification'
      });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        setView('login');
        resetForm();
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-3813">
      <header className="bg-white border-b border-gray-200" data-id="element-3814">
        <div className="max-w-md mx-auto px-5 py-4" data-id="element-3815">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-3816">
            <ArrowLeft size={16} className="mr-1" data-id="element-3817" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-3818">
        <div className="w-full max-w-md" data-id="element-3819">
          <div className="bg-white rounded-xl shadow-sm p-8" data-id="element-3820">
            <h1 className="text-2xl font-bold mb-2 text-center" data-id="element-3821">
              {view === 'signup' ? 'Create Account' : view === 'login' ? 'Welcome Back' : 'Reset Password'}
            </h1>
            <p className="text-gray-600 text-sm text-center mb-6" data-id="element-3822">
              {view === 'signup' ? 'Start analyzing conversations for toxic patterns' : view === 'login' ? 'Sign in to your ToxicDetector account' : !otpSent ? 'Enter your email to receive a reset code' : !resetOtpVerified ? 'Enter the code sent to your email' : 'Create your new password'}
            </p>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2" data-id="element-3823">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" data-id="element-3824" />
                <div className="flex-1" data-id="element-3825">{error}</div>
              </div>}

            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm" data-id="element-3826">
                {success}
              </div>}

            {/* Google Sign-In Button - Show on login and signup (not on forgot password or OTP) */}
            {view !== 'forgot' && !otpSent && <>
                <GoogleSignInButton onClick={handleGoogleSignIn} loading={googleLoading} text={view === 'signup' ? 'Sign up with Google' : 'Sign in with Google'} data-id="element-3827" />

                <div className="relative my-6" data-id="element-3828">
                  <div className="absolute inset-0 flex items-center" data-id="element-3829">
                    <div className="w-full border-t border-gray-300" data-id="element-3830"></div>
                  </div>
                  <div className="relative flex justify-center text-sm" data-id="element-3831">
                    <span className="px-2 bg-white text-gray-500" data-id="element-3832">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </>}

            {view === 'signup' && !otpSent && <form onSubmit={handleSignup} className="space-y-4" data-id="element-3833">
                <div className="grid grid-cols-2 gap-3" data-id="element-3834">
                  <div data-id="element-3835">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3836">
                      First Name
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3837" />
                  </div>
                  <div data-id="element-3838">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3839">
                      Last Name
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3840" />
                  </div>
                </div>

                <div data-id="element-3841">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3842">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3843" />
                </div>

                <div data-id="element-3844">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3845">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => {
                setPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3846" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3847">
                    <p className="font-medium text-gray-700" data-id="element-3848">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3849">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3850">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3851" /> : <X size={16} className="mr-2" data-id="element-3852" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3853">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3854" /> : <X size={16} className="mr-2" data-id="element-3855" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3856">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3857" /> : <X size={16} className="mr-2" data-id="element-3858" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3859">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3860" /> : <X size={16} className="mr-2" data-id="element-3861" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3862">
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3863">
                  Already have an account?{' '}
                  <Link to="/login" className="text-purple-600 font-medium hover:underline" data-id="element-3864">
                    Sign in
                  </Link>
                </p>

                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-3865">
                  By signing up, you agree to our{' '}
                  <a href="/terms" className="text-purple-600 hover:underline" data-id="element-3866">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-purple-600 hover:underline" data-id="element-3867">
                    Privacy Policy
                  </a>
                </p>
              </form>}

            {view === 'signup' && otpSent && <form onSubmit={handleVerifyOtp} className="space-y-4" data-id="element-3868">
                <div data-id="element-3869">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3870">
                    Verification Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3871" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3872">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3873">
                  Back to signup
                </button>
              </form>}

            {view === 'login' && <form onSubmit={handleLogin} className="space-y-4" data-id="element-3874">
                <div data-id="element-3875">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3876">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3877" />
                </div>

                <div data-id="element-3878">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3879">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3880" />
                </div>

                <div className="flex items-center justify-between text-sm" data-id="element-3881">
                  <button type="button" onClick={() => setView('forgot')} className="text-purple-600 hover:underline" data-id="element-3882">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3883">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3884">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-purple-600 font-medium hover:underline" data-id="element-3885">
                    Sign up
                  </Link>
                </p>
              </form>}

            {view === 'forgot' && !otpSent && <form onSubmit={handleSendResetOtp} className="space-y-4" data-id="element-3886">
                <div data-id="element-3887">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3888">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3889" />
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3890">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>

                <button type="button" onClick={() => {
              setView('login');
              resetForm();
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3891">
                  Back to login
                </button>
              </form>}

            {view === 'forgot' && otpSent && !resetOtpVerified && <form onSubmit={handleVerifyResetOtp} className="space-y-4" data-id="element-3892">
                <div data-id="element-3893">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3894">
                    Reset Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3895" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3896">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3897">
                  Back to email entry
                </button>
              </form>}

            {view === 'forgot' && resetOtpVerified && <form onSubmit={handleResetPassword} className="space-y-4" data-id="element-3898">
                <div data-id="element-3899">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3900">
                    New Password
                  </label>
                  <input type="password" value={newPassword} onChange={e => {
                setNewPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3901" />
                </div>

                <div data-id="element-3902">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3903">
                    Confirm New Password
                  </label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3904" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3905">
                    <p className="font-medium text-gray-700" data-id="element-3906">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3907">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3908">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3909" /> : <X size={16} className="mr-2" data-id="element-3910" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3911">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3912" /> : <X size={16} className="mr-2" data-id="element-3913" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3914">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3915" /> : <X size={16} className="mr-2" data-id="element-3916" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3917">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3918" /> : <X size={16} className="mr-2" data-id="element-3919" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3920">
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}